import React, { useEffect, useRef, useState } from "react";
import "./ZFaDL.scss";

import { IconButton, Stack, Tooltip } from "@mui/material";
import { AiOutlineFilePdf } from "react-icons/ai";
import {
  CenovaPonuka,
  CenovaPonukaSearchQuery,
  CenovaPonuka as CenovaPonukaType,
} from "../../../ts/CP";
import Table from "../../ui/Table/Table";

import generateCenovaPonuka from "../../../pdf/CenovaPonuka";
import generateDodaciList from "../../../pdf/DodaciList";
import generateZalohovaFaktura from "../../../pdf/ZalohovaFaktura";
import { getObchodnikById } from "../../../utils";
import FakturaPreview, {
  FakturaPreviewType,
} from "../CenovePonuky/FakturaPreview";
import { useQueryWithPopupErrorHandling } from "../../../providers/utils/hooks";
import { ITEMS_PER_PAGE } from "../../ui/LazyDataGrid/common";
import { cenovaPonukaModel } from "../../../api/models/cenovaPonukaModel";
import Input from "../../ui/Input/Input";
import Button from "../../ui/Button/Button";
import { BsSearch } from "react-icons/bs";
import { LazyDataGrid } from "../../ui/LazyDataGrid/LazyDataGrid";
import { MultiLineCell } from "../../ui/LazyDataGrid/MultiLineCell";
import { useNavigate } from "react-router";
import { BiLink } from "react-icons/bi";

interface Props {}

const ZFaDLColumns = [
  { key: "id", visible: false },
  { key: "objednavatel", visible: false },
  { key: "prijimatel", visible: false },
  { key: "zaloha", visible: false },
  { key: "splatnost", visible: false },
  { key: "termin", visible: false },
  { key: "dph", visible: false },
  { key: "rabat", visible: false },
  { key: "bonus", visible: false },
  { key: "platnost", visible: false },
  { key: "dobierka", visible: false },
  { key: "zaloha_platba", visible: false },
  { key: "polozky", visible: false },
  { title: "ČÍSLO PONUKY", key: "cislo_ponuky", width: "20%" },
  { title: "OBJEDNÁVATEĽ", key: "objednavatel_nazev", width: "30%" },
  {
    title: "PRIJÍMATEĽ",
    key: "prijimatel_nazev",
    width: "30%",
  },
  { title: "CENA CELKOM", key: "cena_celkem", width: "10%" },
  { title: "PDF", actions: true, key: "actions", width: "10%" },
];

const ZFaDL: React.FC<Props> = ({}: Props) => {
  const previewRef = useRef<FakturaPreviewType>(null);

  const navigate = useNavigate();
  const { call } = useQueryWithPopupErrorHandling();
  const [data, setData] = useState<CenovaPonuka[]>([]);
  const [totalCount, setTotalCount] = useState(0);
  const [isLoading, setIsLoading] = useState(true);
  const [page, setPage] = useState(0);
  const [pageSize, setPageSize] = useState(ITEMS_PER_PAGE);
  const [query, setQuery] = useState<CenovaPonukaSearchQuery>({
    cislo_ponuky: "",
    objednavatel: "",
    prijimatel: "",
  });

  const [activeQuery, setActiveQuery] =
    useState<CenovaPonukaSearchQuery | null>(null);

  useEffect(() => {
    const getCenovePonuky = async () => {
      setIsLoading(true);
      await call(
        () =>
          cenovaPonukaModel.getFilteredCenovePonuky({
            offset: (page * pageSize).toString(),
            limit: pageSize.toString(),
            query: activeQuery ? JSON.stringify(activeQuery) : undefined,
          }),
        "Zobrazení cenových ponuk nebolo úspešné",
        null,
        ({ data, totalCount }) => {
          setData(data);
          setTotalCount(totalCount);
        },
        undefined,
        true
      );
      setIsLoading(false);
    };

    getCenovePonuky();
  }, [page, activeQuery, pageSize]);

  const searchClick = () => {
    if (
      query.cislo_ponuky === "" &&
      query.objednavatel === "" &&
      query.prijimatel === ""
    )
      return;

    setActiveQuery(query);
    setQuery({
      cislo_ponuky: "",
      objednavatel: "",
      prijimatel: "",
    });
  };

  const inputKeyPress = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === "Enter") {
      searchClick();
    }
  };

  const getFormattedRowData = (row: any): CenovaPonukaType => {
    return row as CenovaPonukaType;
  };

  return (
    <>
      <FakturaPreview ref={previewRef} />
      <div className="zfadl">
        <Stack gap={2} flexDirection="row" flexWrap="wrap" mb={2}>
          <Input
            value={query.cislo_ponuky}
            onChange={(e) =>
              setQuery({ ...query, cislo_ponuky: e.target.value })
            }
            onKeyPress={inputKeyPress}
            borderColor="yellow"
            placeholder="Číslo ponuky"
          />
          <Input
            value={query.objednavatel}
            onChange={(e) =>
              setQuery({ ...query, objednavatel: e.target.value })
            }
            onKeyPress={inputKeyPress}
            borderColor="yellow"
            placeholder="Objednávatel - název"
          />
          <Input
            value={query.prijimatel}
            onChange={(e) => setQuery({ ...query, prijimatel: e.target.value })}
            onKeyPress={inputKeyPress}
            borderColor="yellow"
            placeholder="Prijímateľ - název"
          />
          <Button
            disabled={
              query.cislo_ponuky === "" &&
              query.objednavatel === "" &&
              query.prijimatel === ""
            }
            onClick={searchClick}
            style={{
              display: "flex",
            }}
          >
            {" "}
            <BsSearch
              size={16}
              style={{
                marginRight: 10,
              }}
            />{" "}
            HLADAŤ
          </Button>
          {activeQuery ? (
            <Button
              onClick={() => {
                setActiveQuery(null);
                setPage(0);
              }}
            >
              VYMAZAT FILTR
            </Button>
          ) : null}
        </Stack>

        <LazyDataGrid<CenovaPonuka>
          id="cenove-ponuky-zfadl"
          rows={data}
          isLoading={isLoading}
          totalCount={totalCount}
          onPageChange={setPage}
          onPageSizeChange={setPageSize}
          columns={[
            {
              field: "link",
              headerName: "",
              renderCell: ({ row }) => (
                <Tooltip title="Prejsť na cenovú ponuku">
                  <IconButton
                    onClick={() => navigate(`/cenove-ponuky/${row._id}`)}
                  >
                    <BiLink size={22} color="#333" />
                  </IconButton>
                </Tooltip>
              ),
              width: 65,
            },
            {
              field: "cislo_ponuky",
              headerName: "ČÍSLO PONUKY",
              width: 115,
            },
            {
              field: "objednavatel",
              headerName: "OBJEDNÁVATEĽ",
              renderCell: ({ row }) =>
                row.objednavatel ? (
                  <MultiLineCell>{row.objednavatel.nazev}</MultiLineCell>
                ) : undefined,
              width: 320,
            },
            {
              field: "prijimatel",
              headerName: "PRIJÍMATEĽ",
              renderCell: ({ row }) =>
                row.prijimatel ? (
                  <MultiLineCell>
                    {row.prijimatel.jmeno} {row.prijimatel.prijmeni}
                  </MultiLineCell>
                ) : undefined,
              width: 250,
            },
            {
              field: "cena_celkom",
              headerName: "CENA CELKOM",
              renderCell: ({ row }) => {
                return row.polozky
                  .map((p) => p.cennikova_cena * p.mn)
                  .reduce((a, b) => a + b, 0)
                  .toFixed(2);
              },
              width: 200,
            },
            {
              field: "actions",
              headerName: "PDF",
              flex: 1,
              renderCell: ({ row }) => (
                <Stack
                  direction="row"
                  gap={1}
                  alignItems="center"
                  sx={{ height: "100%" }}
                >
                  <Tooltip title="PDF Cenová ponuka">
                    <IconButton
                      onClick={async () => {
                        const cenovaPonuka = getFormattedRowData(row);

                        if (
                          !cenovaPonuka.last_edited_by ||
                          !cenovaPonuka.last_edited_date
                        ) {
                          cenovaPonuka.last_edited_by = await getObchodnikById(
                            cenovaPonuka.author ? cenovaPonuka.author : ""
                          );
                          cenovaPonuka.last_edited_date = cenovaPonuka.date;
                        }

                        const { url, doc, fileName } =
                          await generateCenovaPonuka(cenovaPonuka);
                        previewRef.current?.showPreview(url, doc, fileName);
                      }}
                    >
                      <AiOutlineFilePdf size={26} color="#333" />
                    </IconButton>
                  </Tooltip>
                  <Tooltip title="PDF Zálohová faktura">
                    <IconButton
                      onClick={async () => {
                        const cenovaPonuka = getFormattedRowData(row);

                        if (
                          !cenovaPonuka.last_edited_by ||
                          !cenovaPonuka.last_edited_date
                        ) {
                          cenovaPonuka.last_edited_by = await getObchodnikById(
                            cenovaPonuka.author ? cenovaPonuka.author : ""
                          );
                          cenovaPonuka.last_edited_date = cenovaPonuka.date;
                        }

                        const { url, doc, fileName } =
                          await generateZalohovaFaktura(cenovaPonuka);
                        previewRef.current?.showPreview(url, doc, fileName);
                      }}
                    >
                      <AiOutlineFilePdf size={26} color="#333" />
                    </IconButton>
                  </Tooltip>
                  <Tooltip title="PDF Dodací list">
                    <IconButton
                      onClick={async () => {
                        const cenovaPonuka = getFormattedRowData(row);

                        if (
                          !cenovaPonuka.last_edited_by ||
                          !cenovaPonuka.last_edited_date
                        ) {
                          cenovaPonuka.last_edited_by = await getObchodnikById(
                            cenovaPonuka.author ? cenovaPonuka.author : ""
                          );
                          cenovaPonuka.last_edited_date = cenovaPonuka.date;
                        }

                        const { url, doc, fileName } =
                          generateDodaciList(cenovaPonuka);
                        previewRef.current?.showPreview(url, doc, fileName);
                      }}
                    >
                      <AiOutlineFilePdf size={26} color="#333" />
                    </IconButton>
                  </Tooltip>
                </Stack>
              ),
              width: 110,
            },
          ]}
        />
      </div>
    </>
  );
};

export default ZFaDL;
